import { ApiError, NetworkError } from "../../api/errors";

/**
 * Media bytes, kept on the device.
 *
 * Signed media URLs are not addresses that stay put: the token is rotated on a
 * schedule and the storage signature underneath it is minted fresh on every
 * redirect, so the browser's own HTTP cache loses the file each time either
 * clock turns over and downloads it again. That is wasteful on a phone, and it
 * is also what makes a picture fail intermittently — every re-download is
 * another chance to catch a lapsed signature or a dropped connection.
 *
 * So the bytes are stored under a key of our own choosing, one that never
 * changes and never gets fetched: an attachment's content is fixed for the life
 * of its id (replacing an artwork's hero mints a new row), which is exactly the
 * invariant a cache needs. An upload seeds this from the file the user just
 * picked, so a work photographed on this device never downloads its own picture.
 *
 * Everything here degrades to "just use the signed url": Cache Storage is
 * absent in an insecure context, disabled in some private modes, and can refuse
 * a write when the quota is full. None of that is allowed to hide a picture.
 */

const CACHE_NAME = "art-media-v1";

export type MediaVariant = "original" | "display" | "thumbnail";

/** A cache key, not an address — nothing ever requests this url. */
function keyFor(attachmentId: string, variant: MediaVariant): string {
  return `${location.origin}/__media/${attachmentId}/${variant}`;
}

async function openCache(): Promise<Cache | null> {
  if (typeof caches === "undefined") return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/**
 * Drops the oldest quarter. `keys()` is insertion-ordered, which is a good
 * enough stand-in for least-recently-used given the only cost of being wrong is
 * one re-download.
 */
async function evictOldest(cache: Cache): Promise<void> {
  const keys = await cache.keys();
  const doomed = keys.slice(0, Math.ceil(keys.length / 4));
  await Promise.all(doomed.map((request) => cache.delete(request)));
}

async function store(cache: Cache, key: string, response: Response): Promise<void> {
  try {
    await cache.put(key, response);
  } catch {
    // Almost always the quota. One eviction pass, one retry, then let it go:
    // this is an optimisation and the signed url still works.
    try {
      await evictOldest(cache);
      await cache.put(key, response);
    } catch {
      /* not cached this time */
    }
  }
}

/**
 * Stores bytes the app already holds.
 *
 * Called at the end of an upload with the very `File` and thumbnail `Blob` that
 * were just sent, so the record the user is about to open paints without a
 * single request.
 */
export async function seed(attachmentId: string, variant: MediaVariant, blob: Blob): Promise<void> {
  const cache = await openCache();
  if (!cache) return;
  await store(
    cache,
    keyFor(attachmentId, variant),
    new Response(blob, {
      headers: { "Content-Type": blob.type || "application/octet-stream" },
    }),
  );
}

/**
 * The cached bytes as an object URL, downloading them once if they are not here
 * yet. Throws `ApiError` carrying the status, so a caller can tell a lapsed
 * signature from an attachment that is genuinely gone.
 *
 * The object URL belongs to the caller and must be revoked when it stops
 * rendering it.
 */
export async function resolve(
  attachmentId: string,
  variant: MediaVariant,
  signedUrl: string,
  signal?: AbortSignal,
): Promise<string> {
  const cache = await openCache();
  const key = keyFor(attachmentId, variant);

  if (cache) {
    const hit = await cache.match(key);
    if (hit) return URL.createObjectURL(await hit.blob());
  }

  let response: Response;
  try {
    // The token in the url is the whole credential; sending cookies would only
    // turn a simple cross-origin GET into a credentialed one.
    response = await fetch(signedUrl, { signal, credentials: "omit" });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new NetworkError("media unreachable");
  }
  if (!response.ok) throw new ApiError(`media ${response.status}`, response.status);

  if (cache) await store(cache, key, response.clone());
  return URL.createObjectURL(await response.blob());
}

/** Drops every variant of one attachment. Call when the row is deleted. */
export async function forget(attachmentId: string): Promise<void> {
  const cache = await openCache();
  if (!cache) return;
  await Promise.all(
    (["original", "display", "thumbnail"] as MediaVariant[]).map((variant) =>
      cache.delete(keyFor(attachmentId, variant)),
    ),
  );
}

/** Forgets everything. Only for a storage panic; nothing calls it on a schedule. */
export async function clear(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    /* nothing to do about it */
  }
}
